import { z } from 'zod';
import { IsoDate, IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS9 — commission & earnings + chair-rent recording (brief 09 §4).
 *
 * Commission RULES per staff member: a default % plus an optional per-service-CATEGORY override,
 * applied OVER the existing per-renter/per-staff payment splits (NOT re-implementing splits). Earnings
 * are COMPUTED on top of RECOGNISED revenue lines — real service/product revenue and voucher
 * REDEMPTION revenue — and NEVER on voucher SALES.
 *
 * CRITICAL money-safety (ADR-0009 §Consequences): a sold gift card is a liability, not revenue, so
 * paying commission on it would pay staff twice. Commission is therefore computed only on the
 * redemption's `revenue` lines (where revenue is recognised once), exactly as takings/KPI reports read
 * `revenue`, never `gift_card_liability`. A voucher SALE generates £0 commission; only its later
 * REDEMPTION revenue does — pinned by the unit tests.
 *
 * Chair-rent RECORDING: a flat weekly/monthly amount per renter so owner reports show the net position
 * per chair. Recording + reporting ONLY — invoicing renters is out of scope for now.
 *
 * RLS is salon-scoped, renter-aware-floor-visible (like payments): a renter sees their OWN commission
 * rule / chair rent / earnings; the owner/manager sees the floor. Money is integer pence; rates are
 * basis points (no floats), consistent with the pence policy.
 */

// ── Shared primitive ────────────────────────────────────────────────────────────

/** A commission rate in basis points (2000 = 20.00%); 0–10000 (100% max). */
export const CommissionRateBps = z
  .int()
  .min(0)
  .max(10000)
  .describe('commission rate in basis points (2000 = 20.00%)');
export type CommissionRateBps = z.infer<typeof CommissionRateBps>;

// ── Commission rule (entity) ─────────────────────────────────────────────────────

/**
 * One commission rule for a staff member. `category` null = the staff member's DEFAULT rate (applied
 * to any revenue line that has no matching category override); `category` set = the per-service-category
 * OVERRIDE that takes precedence for lines in that category. Renter-aware-floor-visible: a renter sees
 * its own rules; the owner/manager sees the floor.
 */
export const CommissionRule = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable().describe(
      "the staff member's own book; null for floor/employed staff",
    ),
    staffId: Uuid.describe('the staff member the rule pays'),
    category: z
      .string()
      .nullable()
      .describe('null = the default rate; set = the per-service-category override'),
    rateBps: CommissionRateBps,
    createdByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'CommissionRule' });
export type CommissionRule = z.infer<typeof CommissionRule>;

/**
 * Set (upsert) a commission rule for a staff member. `category` null sets the default rate; `category`
 * set creates/updates the per-category override. Idempotent in the sense of upsert: re-setting the same
 * (staff, category) replaces the rate (commission config is current-state, not an append-only ledger).
 */
export const SetCommissionRule = z
  .object({
    staffId: Uuid,
    /** Null/omitted = the default rate; a category string = the per-service-category override. */
    category: z.string().trim().min(1).max(80).nullish(),
    rateBps: CommissionRateBps,
  })
  .meta({ id: 'SetCommissionRule' });
export type SetCommissionRule = z.infer<typeof SetCommissionRule>;

export const ListCommissionRulesQuery = z.object({
  staffId: Uuid.optional(),
});
export type ListCommissionRulesQuery = z.infer<typeof ListCommissionRulesQuery>;

// ── Chair rent (entity) ──────────────────────────────────────────────────────────

/** The cadence a flat chair rent recurs on (weekly or monthly). */
export const ChairRentPeriod = z.enum(['weekly', 'monthly']);
export type ChairRentPeriod = z.infer<typeof ChairRentPeriod>;

/**
 * A recorded flat chair rent for a renter — RECORDING ONLY (invoicing is out of scope). One current
 * record per renter (re-setting replaces it). Renter-aware-floor-visible: a renter sees their own; the
 * owner/manager sees the floor and the net-position-per-chair report.
 */
export const ChairRent = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.describe('the renter (chair) the rent is recorded against'),
    amountPence: Pence.describe('the flat rent per period (VAT not modelled — a recorded figure)'),
    period: ChairRentPeriod,
    note: z.string().nullable(),
    createdByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'ChairRent' });
export type ChairRent = z.infer<typeof ChairRent>;

/** Record (upsert) a flat chair rent for a renter. Re-setting the same renter replaces the figure. */
export const SetChairRent = z
  .object({
    renterId: Uuid,
    amountPence: Pence.refine((v) => v > 0, 'amount must be > 0'),
    period: ChairRentPeriod.default('weekly'),
    note: z.string().trim().max(500).nullish(),
  })
  .meta({ id: 'SetChairRent' });
export type SetChairRent = z.infer<typeof SetChairRent>;

// ── Reports ──────────────────────────────────────────────────────────────────────

/**
 * One staff member's earnings over a date range. `revenuePence` is the recognised revenue attributed to
 * the staff member (per-staff payment_splits over succeeded payments PLUS voucher REDEMPTION revenue —
 * NEVER voucher sales). `commissionPence` is the commission computed by the rule resolution over those
 * revenue lines; `earningsPence` equals it (the staff member's pay for the period). `commissionRateBps`
 * is the effective default rate applied (for display).
 */
export const StaffEarningsRow = z
  .object({
    staffId: Uuid,
    staffName: z.string(),
    revenuePence: Pence.describe(
      'recognised revenue attributed to this staff member (never sales)',
    ),
    commissionPence: Pence.describe('commission computed over the revenue lines by the rule'),
    /**
     * WS14 — tips earned this period (sum of kind='tip' receipt lines attributed to this staff member,
     * over succeeded payments in the window). A tip is 100% the staff member's gratuity and is NEVER
     * commissionable — it is loaded SEPARATELY from the commission base and added on top here.
     */
    tipPence: Pence.describe('tips attributed to this staff member (non-commissionable)'),
    earningsPence: Pence.describe(
      'what the staff member earned this period (commissionPence + tipPence)',
    ),
    commissionRateBps: CommissionRateBps.describe("the staff member's effective default rate"),
    /** Chair rent recorded against this staff member's chair, if any (renters only). */
    chairRentPence: Pence.describe('flat chair rent recorded for the period (0 if none)'),
    chairRentPeriod: ChairRentPeriod.nullable(),
  })
  .meta({ id: 'StaffEarningsRow' });
export type StaffEarningsRow = z.infer<typeof StaffEarningsRow>;

export const StaffEarningsReport = z
  .object({
    from: IsoDate,
    to: IsoDate,
    totalRevenuePence: Pence,
    totalCommissionPence: Pence,
    /** WS14 — total tips across all staff in the window (non-commissionable). */
    totalTipsPence: Pence,
    totalEarningsPence: Pence,
    rows: z.array(StaffEarningsRow),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'StaffEarningsReport' });
export type StaffEarningsReport = z.infer<typeof StaffEarningsReport>;

/**
 * The owner's net-position-per-chair report (chair-rent recording made concrete). `grossRevenuePence`
 * is the renter's recognised takings over the range; `commissionPence` is any commission the salon pays
 * on it; `chairRentPence` is the recorded flat rent for the period; `netToSalonPence` is the salon's
 * position per chair (rent + retained margin) — a recorded figure for the owner, not an invoice.
 */
export const ChairRentPositionRow = z
  .object({
    renterId: Uuid,
    renterName: z.string(),
    grossRevenuePence: Pence,
    commissionPence: Pence,
    chairRentPence: Pence,
    chairRentPeriod: ChairRentPeriod.nullable(),
    netToSalonPence: Pence.describe(
      'chair rent + revenue retained by the salon (recorded position)',
    ),
  })
  .meta({ id: 'ChairRentPositionRow' });
export type ChairRentPositionRow = z.infer<typeof ChairRentPositionRow>;

export const ChairRentReport = z
  .object({
    from: IsoDate,
    to: IsoDate,
    totalChairRentPence: Pence,
    totalNetToSalonPence: Pence,
    rows: z.array(ChairRentPositionRow),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'ChairRentReport' });
export type ChairRentReport = z.infer<typeof ChairRentReport>;
